// This file is large, and mostly on purpose: nearly all of it is the two GraphQL subscription
// documents below, which are declarative data rather than logic (the actual logic is the ~70-line
// hook at the bottom). It also resists the obvious split — Relay requires an operation's name to be
// prefixed by its module name, and both `IssueViewerSubscriptionMetadataSubscription` and
// `IssueViewerSubscriptionTimelineSubscription` are prefixed by `IssueViewerSubscription`. Moving
// either document to its own module means renaming a live subscription operation, which changes what
// is sent to the server. It will keep growing as the M3.4 fragment cleanup (github/issues#22622)
// inlines the remaining fragment spreads here; that is expected.
import {cacheForceRefresh} from '@github-ui/relay-query-cache/query-helpers'
import {useMemo, useState} from 'react'
import {graphql, useRelayEnvironment, useSubscription} from 'react-relay'

import type {
  IssueViewerSubscriptionMetadataSubscription,
  IssueViewerSubscriptionMetadataSubscription$data,
} from './__generated__/IssueViewerSubscriptionMetadataSubscription.graphql'
import type {
  IssueViewerSubscriptionTimelineSubscription,
  IssueViewerSubscriptionTimelineSubscription$data,
} from './__generated__/IssueViewerSubscriptionTimelineSubscription.graphql'
import {IssueViewerViewGraphqlQuery} from './IssueViewer'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const metadataSubscription = graphql`
  subscription IssueViewerSubscriptionMetadataSubscription($issueId: ID!) {
    issueUpdated(id: $issueId) {
      issue {
        number
        repository {
          name
          owner {
            login
          }
        }
      }
      deletedCommentId @deleteRecord
      issueMetadataUpdated {
        labels(first: 100, orderBy: {field: NAME, direction: ASC}) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              color
              name
              nameHTML
              description
              url
            }
          }
        }
        milestone {
          id
          title
          closed
          dueOn
          progressPercentage
          url
          closedAt
        }
        ...ProjectsSectionFragment
        # AssigneesSectionAssignees was inlined here when it migrated off Relay.
        # DevelopmentSectionFragment was inlined here when DevelopmentSection migrated off Relay.
        # MilestonesSectionMilestone was inlined here when MilestonesSectionMilestone migrated off Relay.
        # LinkedPullRequests was inlined here when it migrated off Relay.
        linkedPullRequests: closedByPullRequestsReferences(first: 10, includeClosedPrs: false, orderByState: true) {
          nodes {
            repository {
              name
              nameWithOwner
              owner {
                login
              }
            }
            url
            number
            state
            isDraft
          }
        }
        assignedActors(first: 20) {
          nodes {
            ... on Actor {
              __typename
              id
              login
              name
              profileResourcePath
              avatarUrl(size: 64)
              ... on Bot {
                isCopilot
                isAgent
                displayName
              }
            }
          }
        }
        agentAssignments {
          assignee {
            id
            login
            avatarUrl(size: 64)
            isCopilot
            isAgent
            displayName
          }
          assigner {
            __typename
            ... on Node {
              id
            }
            ... on Actor {
              login
            }
          }
        }
        repository {
          id
          databaseId
          name
          owner {
            login
          }
        }
        id
        title
        number
        databaseId
        linkedBranches(first: 25) {
          nodes {
            id
            ref {
              name
              id
              __typename
              target {
                oid
                id
              }
              repository {
                id
                nameWithOwner
                defaultBranchRef {
                  name
                  id
                  target {
                    oid
                    id
                  }
                  associatedPullRequests {
                    totalCount
                  }
                }
              }
              associatedPullRequests {
                totalCount
              }
            }
          }
        }
        closedByPullRequestsReferences(first: 10, includeClosedPrs: true) {
          nodes {
            ... on PullRequest {
              id
              url
              number
              title
              state
              isDraft
              isInMergeQueue
              createdAt
              databaseId
              repository {
                id
                name
                nameWithOwner
                owner {
                  id
                  login
                  __typename
                }
              }
              __typename
            }
          }
        }
        linkedPullRequestReleases(first: 10) {
          nodes {
            pullRequest {
              id
            }
            release {
              id
              tagName
              name
              url
              isPrerelease
              isLatest
            }
          }
        }
        viewerCanLinkBranches
      }
      issueBodyUpdated {
        body
        bodyHTML(unfurlReferences: true)
        bodyVersion
      }
      issueTitleUpdated {
        ...Header
      }
      issueStateUpdated {
        id
        state
        stateReason
        repository {
          id
          nameWithOwner
          owner {
            login
          }
        }
        duplicateOf {
          number
          url
          repository {
            name
            owner {
              login
            }
          }
        }
      }
      issueTypeUpdated {
        repository {
          nameWithOwner
        }
        issueType {
          name
          color
          id
          isEnabled
          description
          pinnedFields {
            __typename
            ... on IssueFieldText {
              id
              name
              dataType
            }
            ... on IssueFieldSingleSelect {
              id
              name
              dataType
            }
            ... on IssueFieldDate {
              id
              name
              dataType
            }
            ... on IssueFieldNumber {
              id
              name
              dataType
            }
          }
        }
      }
      issueReactionUpdated {
        reactionGroups {
          content
          viewerHasReacted
          reactors(first: 5) {
            totalCount
            nodes {
              __typename
              ... on User {
                login
              }
              ... on Bot {
                login
                isCopilot
                isAgent
                displayName
              }
              ... on Organization {
                login
              }
              ... on Mannequin {
                login
              }
            }
          }
        }
      }
      issueFieldsUpdated {
        ...FieldsSectionFieldValues
      }
      commentReactionUpdated {
        reactionGroups {
          content
          viewerHasReacted
          reactors(first: 5) {
            totalCount
            nodes {
              __typename
              ... on User {
                login
              }
              ... on Bot {
                login
                isCopilot
                isAgent
                displayName
              }
              ... on Organization {
                login
              }
              ... on Mannequin {
                login
              }
            }
          }
        }
      }
      commentUpdated {
        ...IssueCommentViewerMarkdownViewer
        ...IssueCommentEditorBodyFragment
        issue {
          ...PinnedCommentDisplay
        }
      }
      pinnedCommentUpdated {
        ...IssueCommentHeader
        issue {
          ...PinnedCommentDisplay
        }
      }
      subIssuesUpdated {
        id
        repository {
          name
          owner {
            login
          }
        }
        subIssuesConnection: subIssues {
          totalCount
        }
        subIssues(first: 100) {
          nodes {
            id
            closed
            state
            stateReason
            issueDependenciesSummary {
              blockedBy
            }
            assignees(first: 10) {
              totalCount
              edges {
                node {
                  id
                  login
                  avatarUrl
                }
              }
            }
            url
            repository {
              name
              owner {
                login
              }
            }
            databaseId
            number
            title
            titleHTML
            issueType {
              id
              name
              color
            }
            subIssuesSummary {
              total
              completed
            }
            closedByPullRequestsReferences(first: 0, includeClosedPrs: true) {
              totalCount
            }
          }
        }
        databaseId
        number
        repository {
          nameWithOwner
          visibility
          viewerCanPush
          owner {
            login
          }
        }
        parent {
          id
        }
        subIssuesSummary {
          total
        }
      }
      issueTransferStateUpdated {
        id
        repository {
          name
          owner {
            login
          }
        }
        subIssuesConnection: subIssues {
          totalCount
        }
        subIssues(first: 100) {
          nodes {
            id
            closed
            state
            stateReason
            issueDependenciesSummary {
              blockedBy
            }
            assignees(first: 10) {
              totalCount
              edges {
                node {
                  id
                  login
                  avatarUrl
                }
              }
            }
            url
            repository {
              name
              owner {
                login
              }
            }
            databaseId
            number
            title
            titleHTML
            issueType {
              id
              name
              color
            }
            subIssuesSummary {
              total
              completed
            }
            closedByPullRequestsReferences(first: 0, includeClosedPrs: true) {
              totalCount
            }
          }
        }
        databaseId
        number
        repository {
          nameWithOwner
          visibility
          viewerCanPush
          owner {
            login
          }
        }
        parent {
          id
        }
        subIssuesSummary {
          total
        }
        ...IssueBodyViewerSubIssues
      }
      # subIssuesSummaryUpdated: no need to subscribe to this here since we calculate that from the subIssues list directly
      parentIssueUpdated {
        # Inlined from the former RelationshipsSectionFragment to keep the Relay store fresh for RelationshipsSectionInternal.
        id
        databaseId
        viewerCanUpdateMetadata
        repository {
          nameWithOwner
          isArchived
          owner {
            login
          }
        }
        topBlockedBy: blockedBy(first: 3, ranked: true) {
          nodes {
            id
            title
            titleHTML
            url
            number
            repository {
              nameWithOwner
              name
              owner {
                login
              }
            }
            state
            stateReason
          }
          pageInfo {
            hasNextPage
          }
        }
        topBlocking: blocking(first: 3, ranked: true) {
          nodes {
            id
            title
            titleHTML
            url
            number
            repository {
              nameWithOwner
              name
              owner {
                login
              }
            }
            state
            stateReason
          }
          pageInfo {
            hasNextPage
          }
        }
        topRelatesTo: relatesTo(first: 3, ranked: true) {
          totalCount
          nodes {
            id
            title
            titleHTML
            url
            number
            repository {
              nameWithOwner
              name
              owner {
                login
              }
            }
            state
            stateReason
          }
          pageInfo {
            hasNextPage
          }
        }
        securityAlerts(first: 100) {
          nodes {
            number
            repository {
              nameWithOwner
            }
            status
            title
          }
        }
        issueDependenciesSummary {
          blockedBy
          blocking
        }
        parent {
          id
          number
          repository {
            name
            nameWithOwner
            owner {
              login
            }
          }
          state
          stateReason
          title
          titleHTML
          url
          subIssuesSummary {
            total
            completed
          }
        }
      }
      issueDependenciesSummaryUpdated {
        # Inlined from the former RelationshipsSectionFragment to keep the Relay store fresh for RelationshipsSectionInternal.
        id
        databaseId
        viewerCanUpdateMetadata
        repository {
          nameWithOwner
          isArchived
          owner {
            login
          }
        }
        parent {
          id
          titleHTML
          url
          number
          repository {
            nameWithOwner
          }
          state
          stateReason
          subIssuesSummary {
            total
            completed
          }
        }
        topBlockedBy: blockedBy(first: 3, ranked: true) {
          nodes {
            id
            title
            titleHTML
            url
            number
            repository {
              nameWithOwner
              name
              owner {
                login
              }
            }
            state
            stateReason
          }
          pageInfo {
            hasNextPage
          }
        }
        topBlocking: blocking(first: 3, ranked: true) {
          nodes {
            id
            title
            titleHTML
            url
            number
            repository {
              nameWithOwner
              name
              owner {
                login
              }
            }
            state
            stateReason
          }
          pageInfo {
            hasNextPage
          }
        }
        topRelatesTo: relatesTo(first: 3, ranked: true) {
          totalCount
          nodes {
            id
            title
            titleHTML
            url
            number
            repository {
              nameWithOwner
              name
              owner {
                login
              }
            }
            state
            stateReason
          }
          pageInfo {
            hasNextPage
          }
        }
        securityAlerts(first: 100) {
          nodes {
            number
            repository {
              nameWithOwner
            }
            status
            title
          }
        }
        state
        issueDependenciesSummary {
          blockedBy
          blocking
        }
        blockedBy(first: 1, ranked: true) {
          nodes {
            titleHTML
            number
            url
            repository {
              name
              owner {
                login
              }
            }
          }
        }
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const timelineSubscription = graphql`
  subscription IssueViewerSubscriptionTimelineSubscription($issueId: ID!, $connections: [ID!]!, $skip: Int) {
    issueUpdated(id: $issueId) {
      issueTimelineUpdated {
        timelineItems(skip: $skip, first: 10, visibleEventsOnly: true) {
          totalCount
          edges @appendEdge(connections: $connections) {
            node {
              __id
              __typename
              ... on SubscribedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on UnsubscribedEvent {
                createdAt
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on MentionedEvent {
                createdAt
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ...IssueComment_issueComment @dangerously_unaliased_fixme
              ... on Reactable {
                reactionGroups {
                  content
                  viewerHasReacted
                  reactors(first: 5) {
                    totalCount
                    nodes {
                      __typename
                      ... on User {
                        login
                      }
                      ... on Bot {
                        login
                        isCopilot
                        isAgent
                        displayName
                      }
                      ... on Organization {
                        login
                      }
                      ... on Mannequin {
                        login
                      }
                    }
                  }
                }
              }
              ... on ClosedEvent {
                databaseId
                createdAt
                stateReason
                duplicateOf {
                  __typename
                  ... on Issue {
                    id
                    titleHTML
                    url
                    number
                    stateReason
                    repository {
                      id
                      name
                      isPrivate
                      owner {
                        login
                      }
                    }
                  }
                  ... on PullRequest {
                    id
                    pullTitleHTML: titleHTML
                    url
                    number
                    state
                    isDraft
                    isInMergeQueue
                    repository {
                      id
                      name
                      isPrivate
                      owner {
                        login
                      }
                    }
                  }
                }
                closingProjectItemStatus
                closer {
                  __typename
                  ... on ProjectV2 {
                    url
                    title
                  }
                  ... on PullRequest {
                    url
                    number
                    repository {
                      id
                      name
                      owner {
                        login
                      }
                    }
                  }
                  ... on Commit {
                    url
                    abbreviatedOid
                    repository {
                      name
                      owner {
                        login
                      }
                    }
                  }
                }
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                intent {
                  rationale
                }
              }
              ... on ReopenedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on LockedEvent {
                databaseId
                createdAt
                lockReason
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on UnlockedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on PinnedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on UnpinnedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on LabeledEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                label {
                  nameHTML
                  name
                  color
                  id
                  description
                }
                intent {
                  rationale
                }
              }
              ... on RenamedTitleEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                currentTitle
                previousTitle
              }
              ... on UnlabeledEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                label {
                  nameHTML
                  name
                  color
                  id
                  description
                }
              }
              ... on UnassignedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                assignee {
                  ... on Actor {
                    login
                  }
                  ... on User {
                    __typename
                    login
                    resourcePath
                    name
                  }
                  ... on Mannequin {
                    __typename
                    login
                    resourcePath
                    name
                  }
                  ... on Organization {
                    __typename
                    login
                    resourcePath
                  }
                  ... on Bot {
                    __typename
                    login
                    resourcePath
                    isCopilot
                    displayName
                    isAgent
                  }
                }
              }
              ... on AssignedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                assignee {
                  ... on Actor {
                    login
                  }
                  ... on User {
                    __typename
                    login
                    resourcePath
                    name
                  }
                  ... on Mannequin {
                    __typename
                    login
                    resourcePath
                    name
                  }
                  ... on Organization {
                    __typename
                    login
                    resourcePath
                  }
                  ... on Bot {
                    __typename
                    login
                    resourcePath
                    isCopilot
                    displayName
                    isAgent
                  }
                }
                intent {
                  rationale
                }
              }
              ... on CommentDeletedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                deletedCommentAuthor {
                  __typename
                  login
                  ... on User {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                }
              }
              ... on UserBlockedEvent {
                databaseId
                createdAt
                blockDuration
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                blockedUser: subject {
                  login
                  name
                }
              }
              ... on CrossReferencedEvent {
                referencedAt
                willCloseTarget
                databaseId
                innerSource: source {
                  __typename
                  ... on Issue {
                    id
                    titleHTML
                    url
                    number
                    stateReason
                    repository {
                      id
                      name
                      isPrivate
                      owner {
                        login
                      }
                    }
                  }
                  ... on PullRequest {
                    id
                    pullTitleHTML: titleHTML
                    url
                    number
                    state
                    isDraft
                    isInMergeQueue
                    repository {
                      id
                      name
                      isPrivate
                      owner {
                        login
                      }
                    }
                  }
                }
              }
              ... on MilestonedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                milestoneTitle
                milestone {
                  url
                }
              }
              ... on DemilestonedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                milestoneTitle
                milestone {
                  url
                }
              }
              ... on ReferencedEvent {
                id
                databaseId
                willCloseSubject
                subject {
                  __typename
                }
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                commit {
                  message
                  messageHeadlineHTML
                  messageBodyHTML
                  url
                  abbreviatedOid
                  repository {
                    name
                    owner {
                      login
                    }
                    defaultBranch
                  }
                }
                createdAt
              }
              ... on ConnectedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                subject {
                  __typename
                  ... on PullRequest {
                    title
                    url
                    number
                    state
                    isDraft
                    isInMergeQueue
                    repository {
                      name
                      owner {
                        login
                      }
                    }
                  }
                }
              }
              ... on TransferredEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                fromRepository {
                  nameWithOwner
                  url
                }
              }
              ... on DisconnectedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                subject {
                  __typename
                  ... on PullRequest {
                    title
                    url
                    number
                    state
                    isDraft
                    isInMergeQueue
                    repository {
                      name
                      owner {
                        login
                      }
                    }
                  }
                }
              }
              ...MarkedAsDuplicateEvent @dangerously_unaliased_fixme
              ...UnmarkedAsDuplicateEvent @dangerously_unaliased_fixme
              ... on ConvertedToDiscussionEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                discussion {
                  url
                  number
                }
              }
              ... on AddedToProjectV2Event {
                databaseId
                createdAt
                actor {
                  login
                  avatarUrl(size: 64)
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                project {
                  title
                  url
                }
              }
              ... on RemovedFromProjectV2Event {
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                project {
                  title
                  url
                }
              }
              ... on ProjectV2ItemStatusChangedEvent {
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                project {
                  title
                  url
                }
                previousStatus
                status
              }
              ... on ConvertedFromDraftEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
              }
              ... on SubIssueAddedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                subIssue {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                  databaseId
                }
              }
              ... on SubIssueRemovedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                subIssue {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                  databaseId
                }
              }
              ... on ParentIssueAddedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                parent {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                  databaseId
                }
              }
              ... on ParentIssueRemovedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                parent {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                  databaseId
                }
              }
              ... on BlockedByAddedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                blockingIssue {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                }
              }
              ... on BlockedByRemovedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                blockingIssue {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                }
              }
              ... on BlockingAddedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                blockedIssue {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                }
              }
              ... on BlockingRemovedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                blockedIssue {
                  __typename
                  id
                  titleHTML
                  url
                  number
                  stateReason
                  repository {
                    id
                    name
                    isPrivate
                    owner {
                      login
                    }
                  }
                }
              }
              ... on IssueTypeAddedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                issueType {
                  name
                  color
                }
                intent {
                  rationale
                }
              }
              ... on IssueTypeRemovedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                issueType {
                  name
                  color
                }
              }
              ... on IssueTypeChangedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                issueType {
                  name
                  color
                }
                prevIssueType {
                  name
                  color
                }
                intent {
                  rationale
                }
              }
              ... on IssueFieldChangedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                issueField {
                  ... on IssueFieldText {
                    dataType
                    name
                  }
                  ... on IssueFieldDate {
                    name
                    dataType
                  }
                  ... on IssueFieldNumber {
                    name
                    dataType
                  }
                  ... on IssueFieldSingleSelect {
                    name
                    dataType
                  }
                  ... on IssueFieldMultiSelect {
                    name
                    dataType
                  }
                }
                newColor
                newOptions {
                  name
                  color
                }
                newValue
                intent {
                  rationale
                }
              }
              ... on IssueFieldRemovedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                issueField {
                  ... on IssueFieldText {
                    dataType
                    name
                  }
                  ... on IssueFieldDate {
                    name
                    dataType
                  }
                  ... on IssueFieldNumber {
                    name
                    dataType
                  }
                  ... on IssueFieldSingleSelect {
                    name
                    dataType
                  }
                  ... on IssueFieldMultiSelect {
                    name
                    dataType
                  }
                }
                intent {
                  rationale
                }
              }
              ... on IssueFieldAddedEvent {
                databaseId
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                createdAt
                issueField {
                  ... on IssueFieldText {
                    dataType
                    name
                  }
                  ... on IssueFieldDate {
                    name
                    dataType
                  }
                  ... on IssueFieldNumber {
                    name
                    dataType
                  }
                  ... on IssueFieldSingleSelect {
                    name
                    dataType
                  }
                  ... on IssueFieldMultiSelect {
                    name
                    dataType
                  }
                }
                options {
                  name
                  color
                }
                value
                color
                intent {
                  rationale
                }
              }
              ... on IssueCommentPinnedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                issueComment {
                  url
                  databaseId
                }
              }
              ... on IssueCommentUnpinnedEvent {
                databaseId
                createdAt
                actor {
                  avatarUrl(size: 64)
                  login
                  profileResourcePath
                  __typename
                  ... on Bot {
                    isCopilot
                    isAgent
                    displayName
                  }
                  ... on User {
                    name
                  }
                  ... on EnterpriseUserAccount {
                    name
                  }
                  ... on Mannequin {
                    name
                  }
                }
                issueComment {
                  url
                  databaseId
                }
              }
            }
          }
        }
      }
    }
  }
`

export const useIssueViewerSubscription = (issueId: string, initialSkip: number | null, connectionId: string) => {
  const relayEnvironment = useRelayEnvironment()
  const [skipCount, setSkipCount] = useState(initialSkip || 0)

  const metadataConfig = useMemo(
    () => ({
      subscription: metadataSubscription,
      variables: {issueId},
      onNext: (resp: IssueViewerSubscriptionMetadataSubscription$data | null | undefined) => {
        if (!resp?.issueUpdated?.issue) return

        // When a comment is deleted, the mutation already handles edge removal
        // and totalCount via its updater. A force refresh here would race with
        // the in-flight mutation even if other metadata fields are also set.
        if (resp.issueUpdated.deletedCommentId) return

        // Only force-refresh when the subscription carries an actual metadata
        // change. Timeline-only events (comment creation, etc.) arrive with no
        // metadata fields set and are already handled by the timeline
        // subscription via @appendEdge. A force refresh in those cases
        // re-fetches with backTimelineItems(last: 0), resetting the Relay back
        // connection and racing with the in-flight @appendEdge — causing
        // comments to disappear or reorder.
        // See: https://github.com/github/issues/issues/19785
        const {issue, deletedCommentId, ...metadataFields} = resp.issueUpdated
        const hasMetadataChanges = Object.values(metadataFields).some(v => v != null)

        if (!hasMetadataChanges) return

        const number = resp.issueUpdated.issue.number
        const repoName = resp.issueUpdated.issue.repository.name
        const repoOwner = resp.issueUpdated.issue.repository.owner.login

        cacheForceRefresh(relayEnvironment, {
          query: IssueViewerViewGraphqlQuery,
          number,
          owner: repoOwner,
          repo: repoName,
        })
      },
    }),
    [issueId, relayEnvironment],
  )

  const timelineConfig = useMemo(
    () => ({
      subscription: timelineSubscription,
      onNext: (resp: IssueViewerSubscriptionTimelineSubscription$data | null | undefined) => {
        if (!resp?.issueUpdated?.issueTimelineUpdated) return

        // Since we only care about events after a certain point, we need to resubscribe with the new skip count if there are newer events
        const newTotalCount = resp.issueUpdated?.issueTimelineUpdated?.timelineItems?.totalCount
        if (newTotalCount != null && newTotalCount > skipCount) {
          setSkipCount(newTotalCount)
        }
      },
      variables: {
        issueId,
        connections: [connectionId],
        skip: skipCount,
      },
    }),
    [issueId, skipCount, connectionId],
  )

  useSubscription<IssueViewerSubscriptionMetadataSubscription>(metadataConfig)
  useSubscription<IssueViewerSubscriptionTimelineSubscription>(timelineConfig)
}
